<template>
  <div>
    <v-row class="px-4 mb-2" align="center" justify="start">
      <v-col cols="12" md="4" class="text-center">
        <RowsPerPage :pagination="pagination" :disabled="isLoading" />
      </v-col>
    </v-row>
    <v-data-table
      :headers="headers"
      :items="suppliers"
      :items-per-page="pagination.per_page"
      :loading="isLoading"
      :loading-text="$t('components.noData.fetchingEntries')"
      hide-default-footer>
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>
            {{ $t('entities.suppliers.title') }}
          </v-toolbar-title>
          <v-divider inset vertical class="mx-4" />
          <small class="mr-2">{{ pagination.total }}</small>
          <RefreshButton :refresh="refresh" :loading="isLoading" />
          <v-spacer />
          <v-btn
            v-if="canUpsertSupplier"
            @click="openUpsertDialog()"
            :disabled="isLoading"
            color="primary">
            {{ $t('miscellaneous.create') }}
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          v-if="canUpsertSupplier"
          @click="openUpsertDialog(item)"
          depressed
          x-small
          dark
          fab
          color="primary"
          class="glow ml-2">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          v-if="canDeleteSupplier"
          @click="openDeleteDialog(item)"
          depressed
          x-small
          dark
          fab
          color="error"
          class="ml-2 glow">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <DataTableFooter :loading="isLoading" :pagination="pagination" />
    <UpsertDialog
      v-if="canUpsertSupplier"
      @supplier:upsert="upsertSupplier"
      @dialog:close="closeUpsertDialog"
      :supplier="supplierToUpsert"
      :is-loading="isLoading"
      :is-opened="isUpsertDialogOpen" />
    <DeleteDialog
      v-if="canDeleteSupplier"
      @delete:confirm="deleteSupplier"
      @dialog:close="closeDeleteDialog"
      :supplier="supplierToDelete"
      :is-loading="isLoading"
      :is-opened="isDeleteDialogOpen" />
  </div>
</template>

<script>
import Supplier from '@/models/shop/Supplier'
import DeleteDialog from './DeleteDialog.vue'
import DataTableFooter from '@/components/crud/DataTableFooter.vue'
import RowsPerPage from '@/components/crud/RowsPerPage.vue'
import isRole from '@/mixins/crud/computed/isRole'
import { paginationWatch } from '@/mixins/crud/watch/pagination.js'
import RefreshButton from '@/components/crud/RefreshButton.vue'
import UpsertDialog from './UpsertDialog.vue'

const resetPagination = () => ({
  search: '',
  current_page: 1,
  from: 1,
  last_page: 0,
  path: '',
  per_page: 10,
  to: 0,
  total: 0,
})

export default {
  name: 'Suppliers',
  components: {
    DataTableFooter,
    RowsPerPage,
    RefreshButton,
    DeleteDialog,
    UpsertDialog,
  },
  mixins: [paginationWatch, isRole],
  data() {
    return {
      suppliers: [],
      supplierToDelete: {},
      supplierToUpsert: {},
      isLoading: false,
      isDeleteDialogOpen: false,
      isUpsertDialogOpen: false,
      options: {},
      pagination: resetPagination(),
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena ? this.$store.getters.arena.id : null
    },
    canUpsertSupplier() {
      return this.isAdmin || this.isManager
    },
    canDeleteSupplier() {
      return this.isAdmin || this.isManager
    },
    headers() {
      const headers = [
        { text: this.$t('generalAttributes.name'), value: 'name' },
        { text: this.$t('generalAttributes.email'), value: 'email' },
        { text: this.$t('generalAttributes.phone'), value: 'phone' },
        { text: this.$t('generalAttributes.city'), value: 'city' },
        { text: this.$t('generalAttributes.country'), value: 'country' },
      ]
      if (this.canUpsertSupplier || this.canDeleteSupplier) {
        headers.push({
          text: this.$t('miscellaneous.actions'),
          value: 'actions',
          sortable: false,
        })
      }
      return headers
    },
  },
  methods: {
    closeDeleteDialog() {
      this.isDeleteDialogOpen = false
      this.supplierToDelete = {}
    },
    closeUpsertDialog() {
      this.isUpsertDialogOpen = false
      this.supplierToUpsert = {}
    },
    async deleteSupplier() {
      if (!this.canDeleteSupplier) return
      this.isLoading = true
      try {
        await Supplier.api.delete(this.arenaId, this.supplierToDelete.id)
        await this.fetchEntities()
        this.closeDeleteDialog()
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
    async fetchEntities(append = this.setParams()) {
      try {
        this.isLoading = true
        const response = await Supplier.api.list(this.arenaId, append)
        const data = response.data
        this.suppliers = data.data.map((it) => new Supplier(it))
        this.updatePagination(data.metadata || data.meta, data.data.length)
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
    openDeleteDialog(supplier) {
      this.supplierToDelete = supplier
      this.isDeleteDialogOpen = true
    },
    openUpsertDialog(supplier = null) {
      this.supplierToUpsert = supplier ? { ...supplier } : {}
      this.isUpsertDialogOpen = true
    },
    refresh() {
      this.pagination = resetPagination()
      this.fetchEntities()
    },
    sanitizePayload(payload) {
      const { id, ...payloadWithoutId } = payload
      return id ? { id, ...payloadWithoutId } : payloadWithoutId
    },
    updatePagination(metadata = null, dataLength = 0) {
      const page = metadata?.page ?? this.pagination.current_page ?? 1
      const size = metadata?.size ?? this.pagination.per_page ?? 10
      const total = metadata?.count ?? metadata?.total ?? dataLength
      const lastPage = size ? Math.ceil((total || 0) / size) : 0
      const from = total ? (page - 1) * size + 1 : 0
      const to = total ? Math.min(from + size - 1, total) : 0
      this.pagination.current_page = page
      this.pagination.per_page = size
      this.pagination.total = total
      this.pagination.last_page = lastPage
      this.pagination.from = from
      this.pagination.to = to
    },
    async upsertSupplier(payload) {
      if (!this.canUpsertSupplier) return
      this.isLoading = true
      const requestPayload = this.sanitizePayload(payload)
      try {
        if (!requestPayload.id) await Supplier.api.create(this.arenaId, payload)
        else await Supplier.api.update(this.arenaId, payload.id, payload)
        await this.fetchEntities()
        return this.closeUpsertDialog()
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
  },
  mounted() {
    return this.fetchEntities()
  },
}
</script>
